import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { fetchWithAuth } from '../services/api';

const GL_OPTIONS = [
  { value: 'maintenance materials', label: '6152 Maintenance Materials' },
  { value: 'turnover materials', label: '6151 Turnover Materials' },
  { value: 'buildings', label: '1710 Buildings' }
];

const formatSignedAmount = (value) => {
  const amount = Number(value) || 0;
  const formatted = Math.abs(amount).toFixed(2);
  return amount < 0 ? `-$${formatted}` : `$${formatted}`;
};

const getDefaultGlForProperty = (property, propertyGlDefaults, amount = 0) => {
  if (!property || property === 'UNKNOWN') {
    return 'maintenance materials';
  }
  if (Number(amount) > 2500) {
    return 'buildings';
  }
  return propertyGlDefaults[property] || 'maintenance materials';
};

const slimTransactionForSubmit = (transaction) => ({
  date: transaction.date,
  amount: transaction.amount,
  description: transaction.description,
  allocated_property: transaction.allocated_property,
  default_gl_label: transaction.default_gl_label
});

const ExpensesPage = () => {
  const [files, setFiles] = useState({
    amexFile: null,
    lowesFile: null,
    pikFile: null
  });
  const [processing, setProcessing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const [propertyGlDefaults, setPropertyGlDefaults] = useState({});
  const [glAccounts, setGlAccounts] = useState({});
  const [manualGlOverrides, setManualGlOverrides] = useState(new Set());
  const [sessionId, setSessionId] = useState(null);

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles.length > 0) {
      setFiles(prev => ({ ...prev, [name]: selectedFiles[0] }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.amexFile) {
      alert('Please select an Amex CSV file');
      return;
    }

    setProcessing(true);
    setError(null);
    setSubmitResult(null);
    try {
      const formData = new FormData();
      formData.append('amex_file', files.amexFile);
      if (files.lowesFile) {
        formData.append('lowes_file', files.lowesFile);
      }
      if (files.pikFile) {
        formData.append('pik_file', files.pikFile);
      }

      const response = await fetchWithAuth('/api/expenses/process', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process files');
      }

      const result = await response.json();
      setTransactions(result.transactions);
      setSummary(result.summary);
      setSessionId(result.session_id);
      setPropertyGlDefaults(result.gl_defaults_by_property || {});

      await loadPivotData(result.transactions, result.gl_defaults_by_property || {});

    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const loadPivotData = async (currentTransactions = transactions, glDefaults = propertyGlDefaults) => {
    try {
      const response = await fetchWithAuth('/api/expenses/generate-pivot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ transactions: currentTransactions })
      });

      if (response.ok) {
        const result = await response.json();
        const initialGlAccounts = {};
        Object.entries(result.pivot_data || {}).forEach(([, properties]) => {
          Object.entries(properties).forEach(([property, amount]) => {
            if (property !== 'UNKNOWN') {
              initialGlAccounts[property] = getDefaultGlForProperty(property, glDefaults, amount);
            }
          });
        });
        setGlAccounts((prev) => {
          const merged = { ...initialGlAccounts };
          manualGlOverrides.forEach((property) => {
            if (prev[property]) {
              merged[property] = prev[property];
            }
          });
          setPivotData({ ...result, glAccounts: merged });
          return merged;
        });
      }
    } catch (err) {
      console.error('Error loading pivot data:', err);
    }
  };

  const handleAllocationChange = async (transactionId, propertyAddress, owner) => {
    try {
      const updatedTransactions = transactions.map(t =>
        t.id === transactionId
          ? {
              ...t,
              allocated_property: propertyAddress,
              allocated_owner: owner,
              manually_assigned: true,
              default_gl_label:
                propertyAddress && propertyAddress !== 'UNKNOWN'
                  ? getDefaultGlForProperty(propertyAddress, propertyGlDefaults, t.amount)
                  : null
            }
          : t
      );

      setTransactions(updatedTransactions);
      await loadPivotData(updatedTransactions);

    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleApprovalStatusChange = (transactionIds, newStatus) => {
    try {
      // Update approval status for selected transactions
      const updatedTransactions = transactions.map(t =>
        transactionIds.includes(t.id)
          ? { ...t, approval_status: newStatus }
          : t
      );

      setTransactions(updatedTransactions);

      // Regenerate pivot data with updated transactions
      loadPivotData(updatedTransactions);

    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleGLAccountChange = useCallback((property, value) => {
    setManualGlOverrides((prev) => new Set([...prev, property]));
    setGlAccounts((prev) => {
      const updated = { ...prev, [property]: value };
      setPivotData((pivot) => (pivot ? { ...pivot, glAccounts: updated } : pivot));
      return updated;
    });
  }, []);

  const exportableCount = useMemo(() => {
    const properties = new Set();
    transactions.forEach((t) => {
      const addr = t.allocated_property;
      if (addr && addr !== 'UNKNOWN') {
        properties.add(addr);
      }
    });
    return properties.size;
  }, [transactions]);

  const handleExport = async () => {
    try {
      setExportLoading(true);
      setError(null);
      const response = await fetchWithAuth('/api/expenses/export-bill', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transactions,
          glAccounts
        })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to export files');
      }

      // Download bill CSV
      const billBlob = new Blob([result.bill_csv_data], { type: 'text/csv' });
      const billUrl = window.URL.createObjectURL(billBlob);
      const billLink = document.createElement('a');
      billLink.style.display = 'none';
      billLink.href = billUrl;
      billLink.download = result.bill_filename;
      document.body.appendChild(billLink);
      billLink.click();
      window.URL.revokeObjectURL(billUrl);
      document.body.removeChild(billLink);

      // Download processed Amex activity CSV
      const amexBlob = new Blob([result.amex_csv_data], { type: 'text/csv' });
      const amexUrl = window.URL.createObjectURL(amexBlob);
      const amexLink = document.createElement('a');
      amexLink.style.display = 'none';
      amexLink.href = amexUrl;
      amexLink.download = result.amex_filename;
      document.body.appendChild(amexLink);
      amexLink.click();
      window.URL.revokeObjectURL(amexUrl);
      document.body.removeChild(amexLink);

      alert(`Files exported successfully!\nBill: ${result.bill_filename}\nActivity: ${result.amex_filename}\nProperties: ${result.total_properties}\nTotal Amount: ${formatSignedAmount(result.total_amount)}`);
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handlePostToAppFolio = async () => {
    if (exportableCount === 0) {
      setError('Assign at least one transaction to a property before posting.');
      return;
    }

    try {
      setPostLoading(true);
      setError(null);
      setSubmitResult(null);

      const formData = new FormData();
      formData.append(
        'reviewed',
        JSON.stringify({
          transactions: transactions.map(slimTransactionForSubmit),
          glAccounts
        })
      );

      const response = await fetchWithAuth('/api/expenses/amex/submit', {
        method: 'POST',
        body: formData
      });

      if (!response) {
        throw new Error('Session expired — please sign in again.');
      }

      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error(`Post failed (HTTP ${response.status})`);
      }

      if (result.status === 'failed') {
        setSubmitResult(result);
        throw new Error(
          result.stage ? `Failed at ${result.stage}: ${result.error}` : result.error
        );
      }
      if (!response.ok) {
        throw new Error(result.error || 'Failed to post bill');
      }
      setSubmitResult(result);
    } catch (err) {
      setError(err.message || 'Post to AppFolio failed');
    } finally {
      setPostLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* File Upload Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Upload Files</h3>
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amex CSV File (Required) *
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Export transactions from the Lowe's / Amex card (Transaction Date, Amount, Description).
              Purchases post as charges; returns stay as credits.
            </p>
            <input
              type="file"
              name="amexFile"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lowes CSV File (Optional)
            </label>
            <input
              type="file"
              name="lowesFile"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pik-A-Panel HTML File (Optional)
            </label>
            <input
              type="file"
              name="pikFile"
              accept=".html,.htm"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <button
            type="submit"
            disabled={processing || !files.amexFile}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {processing ? 'Processing...' : 'Process Files'}
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {submitResult && (
        <div
          className={`border px-4 py-3 rounded-lg text-sm ${
            submitResult.status === 'posted'
              ? 'bg-green-50 border-green-200 text-green-900'
              : submitResult.status === 'skipped'
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {submitResult.status === 'posted' && (
            <p>
              Posted bill to AppFolio (reference: {submitResult.reference}, bill id:{' '}
              {submitResult.bill_id}).
            </p>
          )}
          {submitResult.status === 'skipped' && (
            <p>Skipped: {submitResult.reason} (reference: {submitResult.reference})</p>
          )}
          {submitResult.status === 'failed' && (
            <p>
              Failed at {submitResult.stage}: {submitResult.error}
            </p>
          )}
        </div>
      )}

      {/* Summary Section */}
      {summary && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Processing Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{summary.total_transactions}</div>
              <div className="text-sm text-gray-600">Total Transactions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{formatSignedAmount(summary.total_amount)}</div>
              <div className="text-sm text-gray-600">Total Amount</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{summary.matched_transactions}</div>
              <div className="text-sm text-gray-600">Auto-Matched</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{summary.lowes_matches}</div>
              <div className="text-sm text-gray-600">Lowes Matches</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">{summary.pik_matches}</div>
              <div className="text-sm text-gray-600">Pik-A-Panel Matches</div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Review and Pivot Table */}
      {transactions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Transaction Review */}
          <div className="lg:col-span-2">
            <TransactionReview
              transactions={transactions}
              onAllocationChange={handleAllocationChange}
              onApprovalStatusChange={handleApprovalStatusChange}
            />
          </div>

          {/* Live Pivot Table */}
          <div className="lg:col-span-1">
            <LivePivotTable
              pivotData={pivotData}
              glAccounts={glAccounts}
              propertyGlDefaults={propertyGlDefaults}
              onExport={handleExport}
              onPost={handlePostToAppFolio}
              exportLoading={exportLoading}
              postLoading={postLoading}
              exportableCount={exportableCount}
              onGLAccountChange={handleGLAccountChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Transaction Review Component with Approval Workflow
const TransactionReview = ({ transactions, onAllocationChange, onApprovalStatusChange }) => {
  const [sortBy, setSortBy] = useState('none');
  const [collapsedSections, setCollapsedSections] = useState({ approved: true, potential: false, unknown: false });
  const [selectedTransactions, setSelectedTransactions] = useState({ approved: [], potential: [], unknown: [] });

  const categories = useMemo(() => {
    const grouped = { approved: [], potential: [], unknown: [] };

    transactions.forEach((transaction) => {
      if (transaction.approval_status === 'approved') {
        grouped.approved.push(transaction);
      } else if (
        transaction.approval_status === 'potential' ||
        (!transaction.approval_status && transaction.confidence_score >= 70)
      ) {
        grouped.potential.push(transaction);
      } else {
        grouped.unknown.push(transaction);
      }
    });

    return grouped;
  }, [transactions]);

  const sortedCategories = useMemo(() => {
    const sortList = (list) => {
      if (sortBy === 'none') {
        return list;
      }

      const sorted = [...list];

      if (sortBy === 'confidence') {
        sorted.sort((a, b) => b.confidence_score - a.confidence_score);
      } else if (sortBy === 'date') {
        sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      } else if (sortBy === 'amount') {
        sorted.sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
      }

      return sorted;
    };

    return {
      approved: sortList(categories.approved),
      potential: sortList(categories.potential),
      unknown: sortList(categories.unknown),
    };
  }, [categories, sortBy]);

  const toggleSection = (section) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleTransactionSelect = (transactionId, category, isSelected) => {
    setSelectedTransactions((prev) => ({
      ...prev,
      [category]: isSelected
        ? [...prev[category], transactionId]
        : prev[category].filter((id) => id !== transactionId),
    }));
  };

  const handleSelectAll = (category, isSelected) => {
    const categoryTransactions = sortedCategories[category];
    setSelectedTransactions((prev) => ({
      ...prev,
      [category]: isSelected ? categoryTransactions.map((t) => t.id) : [],
    }));
  };

  const moveTransactions = (fromCategory, toCategory) => {
    const selectedIds = selectedTransactions[fromCategory];
    if (selectedIds.length === 0) return;

    onApprovalStatusChange(selectedIds, toCategory);

    setSelectedTransactions((prev) => ({ ...prev, [fromCategory]: [] }));
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">Transaction Review</h3>
            <p className="text-sm text-gray-600">Review and approve property allocations</p>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="none">Original Order</option>
              <option value="confidence">Confidence Score</option>
              <option value="date">Date (Newest First)</option>
              <option value="amount">Amount (Highest First)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="p-4">
        <CategorySection
          title="Approved"
          category="approved"
          transactions={sortedCategories.approved}
          bgColor="bg-green-50 border-green-200"
          isCollapsed={collapsedSections.approved}
          selectedTransactionIds={selectedTransactions.approved}
          onToggle={() => toggleSection('approved')}
          onSelectAll={(checked) => handleSelectAll('approved', checked)}
          onMove={(destination) => moveTransactions('approved', destination)}
          onTransactionSelect={(transactionId, isSelected) => handleTransactionSelect(transactionId, 'approved', isSelected)}
          onAllocationChange={onAllocationChange}
        />
        <CategorySection
          title="Potential"
          category="potential"
          transactions={sortedCategories.potential}
          bgColor="bg-yellow-50 border-yellow-200"
          isCollapsed={collapsedSections.potential}
          selectedTransactionIds={selectedTransactions.potential}
          onToggle={() => toggleSection('potential')}
          onSelectAll={(checked) => handleSelectAll('potential', checked)}
          onMove={(destination) => moveTransactions('potential', destination)}
          onTransactionSelect={(transactionId, isSelected) => handleTransactionSelect(transactionId, 'potential', isSelected)}
          onAllocationChange={onAllocationChange}
        />
        <CategorySection
          title="Unknown"
          category="unknown"
          transactions={sortedCategories.unknown}
          bgColor="bg-red-50 border-red-200"
          isCollapsed={collapsedSections.unknown}
          selectedTransactionIds={selectedTransactions.unknown}
          onToggle={() => toggleSection('unknown')}
          onSelectAll={(checked) => handleSelectAll('unknown', checked)}
          onMove={(destination) => moveTransactions('unknown', destination)}
          onTransactionSelect={(transactionId, isSelected) => handleTransactionSelect(transactionId, 'unknown', isSelected)}
          onAllocationChange={onAllocationChange}
        />
      </div>
    </div>
  );
};
// Individual Transaction Row with Approval Workflow
const TransactionRowWithApproval = ({ transaction, category, isSelected, onSelect, onAllocationChange, isEven }) => {
  const [selectedProperty, setSelectedProperty] = useState(transaction.allocated_property);
  const [selectedOwner, setSelectedOwner] = useState(transaction.allocated_owner);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handlePropertySelect = (address, owner, event) => {
    // Prevent any default behavior that might cause scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setSelectedProperty(address);
    setSelectedOwner(owner);
    setIsDropdownOpen(false);
    setSearchTerm('');
    onAllocationChange(transaction.id, address, owner);
  };


  const getConfidenceColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Filter matches based on search term and confidence threshold
  const filteredMatches = transaction.property_matches?.filter(match => {
    const matchesSearch = match.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         match.owner.toLowerCase().includes(searchTerm.toLowerCase());

    // If user is searching, show all matches that match the search
    // If no search term, only show matches with >50% confidence
    return searchTerm.length > 0 ? matchesSearch : (matchesSearch && match.score > 50);
  }) || [];

  return (
    <div className={`px-6 py-4 ${isEven ? 'bg-gray-50' : 'bg-white'} border-b last:border-b-0`}>
      <div className="grid grid-cols-10 gap-4 items-center">
        {/* Checkbox */}
        <div className="col-span-1">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(e.target.checked);
            }}
            className="rounded"
          />
        </div>

        {/* Transaction Info */}
        <div className="col-span-3">
          <div className="font-medium">{transaction.description}</div>
          <div className={`text-sm ${parseFloat(transaction.amount) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {transaction.date} • {formatSignedAmount(transaction.amount)}
            {parseFloat(transaction.amount) < 0 ? ' return' : ''}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            PO: {transaction.po_info}
          </div>
          {transaction.lowes_matched && (
            <div className="text-xs text-green-600 mt-1">✓ Lowes matched</div>
          )}
          {transaction.pik_matched && (
            <div className="text-xs text-indigo-600 mt-1">✓ Pik-A-Panel matched</div>
          )}
        </div>

        {/* Property Selection */}
        <div className="col-span-3 relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full text-left p-2 border rounded hover:bg-gray-50 flex justify-between items-center"
          >
            <span className="truncate">
              {selectedProperty === 'UNKNOWN' || selectedProperty === 'UNSURE' ? 'Select Property' : selectedProperty}
            </span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b">
                <input
                  type="text"
                  placeholder="Search properties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Property List */}
              <div className="max-h-48 overflow-y-auto overscroll-contain">
                {filteredMatches.map((match, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => handlePropertySelect(match.address, match.owner, e)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b"
                  >
                    <div className="font-medium">{match.address}</div>
                    <div className="text-sm text-gray-600">{match.owner}</div>
                    <div className={`text-xs ${getConfidenceColor(match.score)}`}>
                      {match.score}% confidence
                    </div>
                  </button>
                ))}
                {filteredMatches.length === 0 && searchTerm && (
                  <div className="px-3 py-2 text-gray-500 text-sm">No matching properties found</div>
                )}

                {/* Unknown option at the bottom */}
                <button
                  onClick={(e) => handlePropertySelect('UNKNOWN', 'UNKNOWN', e)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 border-t bg-gray-50"
                >
                  <div className="font-medium text-gray-700">Unknown</div>
                  <div className="text-sm text-gray-500">Deselect property</div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Owner */}
        <div className="col-span-2">
          <div className="text-sm">{selectedOwner || 'UNKNOWN'}</div>
        </div>

        {/* Confidence Score */}
        <div className="col-span-1 text-center">
          <div className={`font-semibold ${getConfidenceColor(transaction.confidence_score)}`}>
            {transaction.confidence_score}%
          </div>
          {transaction.manually_assigned && (
            <div className="text-xs text-blue-600">Manual</div>
          )}
        </div>

      </div>
    </div>
  );
};

// Individual Transaction Row (Original - kept for compatibility)
const TransactionRow = ({ transaction, onAllocationChange, isEven }) => {
  const [selectedProperty, setSelectedProperty] = useState(transaction.allocated_property);
  const [selectedOwner, setSelectedOwner] = useState(transaction.allocated_owner);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handlePropertySelect = (address, owner, event) => {
    // Prevent any default behavior that might cause scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setSelectedProperty(address);
    setSelectedOwner(owner);
    setIsDropdownOpen(false);
    setSearchTerm('');
    onAllocationChange(transaction.id, address, owner);
  };

  const getConfidenceColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Filter matches based on search term and confidence threshold
  const filteredMatches = transaction.property_matches?.filter(match => {
    const matchesSearch = match.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         match.owner.toLowerCase().includes(searchTerm.toLowerCase());

    // If user is searching, show all matches that match the search
    // If no search term, only show matches with >50% confidence
    return searchTerm.length > 0 ? matchesSearch : (matchesSearch && match.score > 50);
  }) || [];

  return (
    <div className={`px-6 py-4 ${isEven ? 'bg-gray-50' : 'bg-white'} border-b last:border-b-0`}>
      <div className="grid grid-cols-10 gap-4 items-center">
        {/* Transaction Info */}
        <div className="col-span-4">
          <div className="font-medium">{transaction.description}</div>
          <div className={`text-sm ${parseFloat(transaction.amount) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {transaction.date} • {formatSignedAmount(transaction.amount)}
            {parseFloat(transaction.amount) < 0 ? ' return' : ''}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            PO: {transaction.po_info}
          </div>
          {transaction.lowes_matched && (
            <div className="text-xs text-green-600 mt-1">✓ Lowes matched</div>
          )}
          {transaction.pik_matched && (
            <div className="text-xs text-indigo-600 mt-1">✓ Pik-A-Panel matched</div>
          )}
        </div>

        {/* Property Selection */}
        <div className="col-span-3 relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full text-left p-2 border rounded hover:bg-gray-50 flex justify-between items-center"
          >
            <span className="truncate">
              {selectedProperty === 'UNKNOWN' || selectedProperty === 'UNSURE' ? 'Select Property' : selectedProperty}
            </span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b">
                <input
                  type="text"
                  placeholder="Search properties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Property List */}
              <div className="max-h-48 overflow-y-auto overscroll-contain">
                {filteredMatches.map((match, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => handlePropertySelect(match.address, match.owner, e)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b"
                  >
                    <div className="font-medium">{match.address}</div>
                    <div className="text-sm text-gray-600">{match.owner}</div>
                    <div className={`text-xs ${getConfidenceColor(match.score)}`}>
                      {match.score}% confidence
                    </div>
                  </button>
                ))}
                {filteredMatches.length === 0 && searchTerm && (
                  <div className="px-3 py-2 text-gray-500 text-sm">No matching properties found</div>
                )}

                {/* Unknown option at the bottom */}
                <button
                  onClick={(e) => handlePropertySelect('UNKNOWN', 'UNKNOWN', e)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 border-t bg-gray-50"
                >
                  <div className="font-medium text-gray-700">Unknown</div>
                  <div className="text-sm text-gray-500">Deselect property</div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Owner */}
        <div className="col-span-2">
          <div className="text-sm">{selectedOwner || 'UNKNOWN'}</div>
        </div>

        {/* Confidence Score */}
        <div className="col-span-1 text-center">
          <div className={`font-semibold ${getConfidenceColor(transaction.confidence_score)}`}>
            {transaction.confidence_score}%
          </div>
          {transaction.manually_assigned && (
            <div className="text-xs text-blue-600">Manual</div>
          )}
        </div>

      </div>
    </div>
  );
};

const CategorySection = ({
  title,
  category,
  transactions,
  bgColor,
  isCollapsed,
  selectedTransactionIds,
  onToggle,
  onSelectAll,
  onMove,
  onTransactionSelect,
  onAllocationChange,
}) => {
  const totalCount = transactions.length;
  const selectedCount = selectedTransactionIds.length;

  return (
    <div className="mb-4 border rounded-lg">
      <div
        className={`${bgColor} px-4 py-3 border-b flex justify-between items-center cursor-pointer`}
        onClick={onToggle}
      >
        <div className="flex items-center space-x-3">
          <button
            type="button"
            aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${title}`}
            className={`text-lg text-gray-700 transform transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <h3 className="font-semibold">{title}</h3>
          <span className="text-sm text-gray-600">({totalCount} transactions)</span>
          {selectedCount > 0 && (
            <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
              {selectedCount} selected
            </span>
          )}
        </div>

        {!isCollapsed && totalCount > 0 && (
          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedCount === totalCount && totalCount > 0}
              onChange={(e) => onSelectAll(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Select All</span>

            {category === 'potential' && selectedCount > 0 && (
              <>
                <button
                  onClick={() => onMove && onMove('approved')}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 mr-2"
                >
                  Move to Approved
                </button>
                <button
                  onClick={() => onMove && onMove('unknown')}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                >
                  Move to Unknown
                </button>
              </>
            )}
            {category === 'unknown' && selectedCount > 0 && (
              <>
                <button
                  onClick={() => onMove && onMove('approved')}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 mr-2"
                >
                  Move to Approved
                </button>
                <button
                  onClick={() => onMove && onMove('potential')}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >
                  Move to Potential
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {!isCollapsed && (
        <>
          <div className="bg-gray-50 px-6 py-3 border-b grid grid-cols-10 gap-4 text-sm font-medium text-gray-700">
            <div className="col-span-1">Select</div>
            <div className="col-span-3">Transaction</div>
            <div className="col-span-3">Property</div>
            <div className="col-span-2">Owner</div>
            <div className="col-span-1 text-center">Confidence</div>
          </div>

          <div className="max-h-96 overflow-y-auto overscroll-contain">
            {transactions.map((transaction, index) => (
              <TransactionRowWithApproval
                key={transaction.id}
                transaction={transaction}
                category={category}
                isSelected={selectedTransactionIds.includes(transaction.id)}
                onSelect={(isSelected) => onTransactionSelect(transaction.id, isSelected)}
                onAllocationChange={onAllocationChange}
                isEven={index % 2 === 0}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};




// Live Pivot Table Component
const LivePivotTable = ({
  pivotData,
  glAccounts,
  propertyGlDefaults,
  onExport,
  onPost,
  exportLoading,
  postLoading,
  exportableCount,
  onGLAccountChange
}) => {
  if (!pivotData) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b flex justify-between items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">Allocation Summary</h3>
          <p className="text-sm text-gray-600">By owner and property (Overbrook Management Group)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onExport}
            disabled={exportLoading || postLoading || exportableCount === 0}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 text-sm"
          >
            {exportLoading ? 'Exporting...' : 'Export Bill'}
          </button>
          <button
            onClick={onPost}
            disabled={postLoading || exportLoading || exportableCount === 0}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
          >
            {postLoading ? 'Posting...' : 'Post to AppFolio'}
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="space-y-4">
          {Object.entries(pivotData.pivot_data).map(([owner, properties]) => {
            const ownerSubtotal = Object.values(properties).reduce((sum, amount) => sum + amount, 0);

            return (
              <div key={owner} className="border-b pb-3 last:border-b-0">
                <div className="font-semibold text-gray-800 mb-2 flex justify-between items-center">
                  <span>{owner}</span>
                  <span className="text-blue-600 font-bold">{formatSignedAmount(ownerSubtotal)}</span>
                </div>
                {Object.entries(properties).map(([property, amount]) => {
                  const isUnknown = property === 'UNKNOWN';
                  return (
                    <div key={property} className="flex justify-between items-center text-sm pl-4 py-1 gap-3">
                      <div className="flex-1">
                        <span className={isUnknown ? 'text-orange-700' : 'text-gray-600'}>{property}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`font-medium ${amount < 0 ? 'text-red-600' : ''}`}>{formatSignedAmount(amount)}</span>
                        {!isUnknown ? (
                          <select
                            value={glAccounts[property] || getDefaultGlForProperty(property, propertyGlDefaults, amount)}
                            onChange={(e) => onGLAccountChange(property, e.target.value)}
                            className="text-xs p-1 border rounded"
                          >
                            {GL_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-orange-700">Not exportable</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span>{formatSignedAmount(pivotData.total_amount)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesPage;